/**
 * Application Configuration
 * Central configuration for the Afikim Water application
 * Auto-detects local vs production environment
 * With detailed startup logging
 */

const LOG_PREFIX = '[Config]';

// Detect if running locally or via Cloudflare tunnel
const isLocal = window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1' ||
                window.location.hostname === '10.0.0.1';

// Check if accessed via afikim.my or afikim-water.org domains (all served from same backend)
const isAfkimDomain = window.location.hostname.includes('afikim.my') ||
                      window.location.hostname.includes('afikim-water.org');

// All afikim.my domains now use same origin (served from same backend)
const isServedFromBackend = isAfkimDomain;

// API Base URL - switches automatically between local and production
const getApiBaseUrl = () => {
  console.log(`${LOG_PREFIX} Determining API Base URL...`);
  console.log(`${LOG_PREFIX}   Hostname: ${window.location.hostname}`);
  console.log(`${LOG_PREFIX}   Protocol: ${window.location.protocol}`);
  console.log(`${LOG_PREFIX}   isLocal: ${isLocal}`);
  console.log(`${LOG_PREFIX}   isAfkimDomain: ${isAfkimDomain}`);
  console.log(`${LOG_PREFIX}   isServedFromBackend: ${isServedFromBackend}`);
  console.log(`${LOG_PREFIX}   REACT_APP_API_URL: ${process.env.REACT_APP_API_URL || 'not set'}`);

  if (process.env.REACT_APP_API_URL) {
    console.log(`${LOG_PREFIX}   Using env variable: ${process.env.REACT_APP_API_URL}`);
    return process.env.REACT_APP_API_URL;
  }

  if (isLocal) {
    console.log(`${LOG_PREFIX}   Using local: http://localhost:3000`);
    return 'http://localhost:3000';
  }

  // All afikim.my domains (afikim.my, www.afikim.my, api.afikim.my)
  // are served from same backend - use same origin (empty string)
  if (isServedFromBackend) {
    console.log(`${LOG_PREFIX}   Using same origin (all domains served from backend)`);
    return '';
  }

  // Fallback - use same origin
  console.log(`${LOG_PREFIX}   Fallback: same origin`);
  return '';
};

// WebSocket URL - dynamic based on environment
const getWsBaseUrl = () => {
  if (isLocal) {
    return 'ws://localhost:3000';
  }

  // All afikim.my domains use same origin WebSocket
  if (isServedFromBackend) {
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${window.location.host}`;
  }

  // Fallback - same origin
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${window.location.host}`;
};

const config = {
  // API Configuration
  api: {
    baseUrl: getApiBaseUrl(),
    apiPath: '/api',
    fullApiUrl: `${getApiBaseUrl()}/api`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  },

  // WebSocket Configuration
  ws: {
    baseUrl: getWsBaseUrl(),
  },

  // Environment
  isLocal,
  isProduction: !isLocal,

  // App Configuration
  app: {
    name: 'Afikim Water',
    version: '1.0.0',
    environment: process.env.NODE_ENV || 'development',
  },

  // Pagination
  pagination: {
    defaultPageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
  },

  // Layout
  layout: {
    sidebarWidth: 240,
    headerHeight: 44,
  },

  // Features
  features: {
    enableSetup: true,
    enableRecords: true,
    enableReports: false,
    enableDashboards: false,
  },

  // Storage
  storage: {
    prefix: 'sf_clone_',
    tokenKey: 'auth_token',
    userKey: 'current_user',
  },
};

// Log configuration on startup (always, for debugging)
console.log('========================================');
console.log(`${LOG_PREFIX} STARTUP`);
console.log('========================================');
console.log(`${LOG_PREFIX} Environment: ${isLocal ? 'LOCAL' : 'PRODUCTION'}`);
console.log(`${LOG_PREFIX} API Base: ${config.api.baseUrl || '(same origin)'}`);
console.log(`${LOG_PREFIX} Full API URL: ${config.api.fullApiUrl}`);
console.log(`${LOG_PREFIX} WebSocket URL: ${config.ws.baseUrl}`);
console.log(`${LOG_PREFIX} Timeout: ${config.api.timeout}ms`);
console.log(`${LOG_PREFIX} App Version: ${config.app.version}`);
console.log(`${LOG_PREFIX} Build Env: ${config.app.environment}`);
console.log('========================================');

export default config;

// Export helper for getting API URL
export const getApiUrl = () => config.api.baseUrl;

// Export connection test function
export const testConnection = async () => {
  const startTime = performance.now();
  console.log(`${LOG_PREFIX} Testing connection to ${config.api.baseUrl}...`);

  try {
    const response = await fetch(`${config.api.baseUrl}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const duration = Math.round(performance.now() - startTime);

    if (response.ok) {
      const data = await response.json();
      console.log(`${LOG_PREFIX} Connection OK (${duration}ms)`, data);
      return { success: true, duration, data };
    } else {
      console.error(`${LOG_PREFIX} Connection FAILED: HTTP ${response.status} (${duration}ms)`);
      return { success: false, duration, status: response.status };
    }
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    console.error(`${LOG_PREFIX} Connection ERROR (${duration}ms):`, error.message);
    return { success: false, duration, error: error.message };
  }
};
